import { useEffect, useState, type ReactNode } from 'react'
import { BarraFerramentasCenario } from '../ribbon/BarraFerramentasCenario'
import type { FerramentasModulo } from '../modulos/tipos'
import { IconePonto, IconeSelecao } from '../icones'
import type { ControleCamera } from '../../visualizacao3d/camera/comandosCamera'
import type { VolumeEnquadramento } from '../../visualizacao3d/camera/comandosCamera'
import { Visualizador3D } from '../../visualizacao3d/Visualizador3D'
import { BarraAplicacao, type Tema } from './BarraAplicacao'
import { BarraEstado, type ItemEstado } from './BarraEstado'
import { DockCamera3D } from './DockCamera3D'
import { DockMobile } from './DockMobile'
import { InspetorCAD } from './InspetorCAD'
import { moduloPorId, type IdModulo } from './modulos'
import { TrilhaFerramentas, type Ferramenta } from './TrilhaFerramentas'
import type { ModoLayout } from './useModoLayout'
import { Workspace } from './Workspace'
import { AVISO_DIDATICO, AVISO_DIDATICO_CURTO } from '../../dominio/avisos'

/** O que é igual em todas as oficinas e vem de `App`. */
export interface ContextoCasca {
  idModulo: IdModulo
  aoTrocarModulo: (id: IdModulo) => void
  tema: Tema
  aoAlternarTema: () => void
  modoLayout: ModoLayout
  camera: ControleCamera
}

/**
 * O que cada oficina entrega para a casca montar a tela.
 *
 * A oficina é dona do seu estado e descreve a tela; a casca decide onde cada
 * pedaço mora no desktop e no celular. É o contrato que substitui o `App.tsx`
 * monolítico do CSV Lab.
 */
export interface OficinaAtiva {
  ferramentas: FerramentasModulo
  /** Conteúdo 3D, dentro do Canvas. */
  cena: ReactNode
  volume: VolumeEnquadramento
  distanciaMaxima?: number
  distanciaMinima?: number
  orbitaHabilitada?: boolean
  cameraLivre?: boolean
  corFundo?: string
  neblinaM?: readonly [number, number]
  /** Elementos de interface sobre o canvas (legenda, banner de posicionamento). */
  sobreposicao?: ReactNode
  /** Fecha a folha de ferramentas quando começa uma demonstração na cena. */
  recolherFerramentas?: boolean
  /** Aba "Operação" do inspetor, quando a oficina tem um roteiro. */
  operacao?: ReactNode
  /** Aba "Checklist" do inspetor. */
  checklist?: ReactNode
  /** Itens extras da barra de estado, depois de módulo e seleção. */
  itensEstado?: ItemEstado[]
  /** Nome do que está selecionado, para a barra de estado. */
  nomeSelecionado?: string | null
  idSelecionado?: string | null
  aoClicarVazio?: () => void
  /** O aluno/professor assumiu a órbita manualmente — a oficina pode cancelar um acompanhamento automático de câmera. */
  aoInteragirManualCamera?: () => void
  historico?: {
    desfazer: () => void
    refazer: () => void
    podeDesfazer: boolean
    podeRefazer: boolean
  }
  ferramentasTrilha?: Array<{ id: string; rotulo: string; ferramentas: Ferramenta[] }>
  comEnquadrarCena?: boolean
}


export function CascaOficina({ contexto, oficina }: { contexto: ContextoCasca; oficina: OficinaAtiva }) {
  const { idModulo, modoLayout, camera } = contexto
  const modulo = moduloPorId(idModulo)
  const [grupoAtivo, setGrupoAtivo] = useState<string | null>(null)
  const [inspetorRecolhido, setInspetorRecolhido] = useState(false)
  const { ferramentas } = oficina
  useEffect(() => {
    if (oficina.recolherFerramentas) setGrupoAtivo(null)
  }, [oficina.recolherFerramentas])

  const itensEstado: ItemEstado[] = [
    { id: 'modulo', rotulo: 'Módulo', valor: modulo.titulo, icone: <modulo.Icone />, tom: 'info' },
    {
      id: 'selecao',
      rotulo: 'Seleção',
      valor: oficina.nomeSelecionado ?? 'nada selecionado',
      icone: oficina.nomeSelecionado ? <IconeSelecao /> : <IconePonto />,
    },
    ...(oficina.itensEstado ?? []),
  ]

  return (
    <Workspace
      tema={contexto.tema}
      modoLayout={modoLayout}
      barraAplicacao={
        <BarraAplicacao
          moduloAtual={idModulo}
          aoTrocarModulo={contexto.aoTrocarModulo}
          tema={contexto.tema}
          aoAlternarTema={contexto.aoAlternarTema}
          modoLayout={modoLayout}
        />
      }
      ribbon={
        modoLayout === 'mobile' ? null : (
          <BarraFerramentasCenario
            titulo={`Ferramentas de ${modulo.titulo}`}
            grupos={ferramentas.grupos}
            grupoAtivo={grupoAtivo}
            aoMudarGrupo={setGrupoAtivo}
            chips={ferramentas.chips}
            acoesRapidas={ferramentas.acoesRapidas}
            chaveContexto={idModulo}
          />
        )
      }
      dock={
        modoLayout === 'mobile' ? (
          <DockMobile
            grupos={ferramentas.grupos}
            grupoAtivo={grupoAtivo}
            aoMudarGrupo={setGrupoAtivo}
            tituloModulo={modulo.titulo}
            acoesRapidas={ferramentas.acoesRapidas}
            inspetor={ferramentas.inspetor ?? null}
            ferramentas={oficina.ferramentasTrilha?.flatMap((grupo) => grupo.ferramentas)}
          />
        ) : undefined
      }
      canvas={
        <>
          <Visualizador3D
            comandoCamera={camera.comando}
            volume={oficina.volume}
            distanciaMaxima={oficina.distanciaMaxima}
            distanciaMinima={oficina.distanciaMinima}
            orbitaHabilitada={oficina.orbitaHabilitada}
            cameraLivre={oficina.cameraLivre}
            aoClicarVazio={oficina.aoClicarVazio}
            aoInteragirManual={oficina.aoInteragirManualCamera}
            corFundo={oficina.corFundo}
            neblinaM={oficina.neblinaM}
          >
            {oficina.cena}
          </Visualizador3D>
          {oficina.sobreposicao}
          {modoLayout === 'mobile' && (
            // no celular o rodapé fica abaixo da dobra; o aviso não pode depender de rolar a página
            <p className="aviso-compacto" title={AVISO_DIDATICO}>
              {AVISO_DIDATICO_CURTO}
            </p>
          )}
        </>
      }
      trilha={
        modoLayout === 'mobile' ? undefined : (
          <TrilhaFerramentas
            camera={{
              executar: (tipo) => camera.executar(tipo, oficina.idSelecionado ?? null),
              vistaAtiva: camera.vistaAtiva,
            }}
            temSelecao={oficina.idSelecionado != null}
            historico={oficina.historico}
            motivoSemHistorico="Esta oficina não tem edição a desfazer: os parâmetros voltam pelos próprios controles"
            modoLayout={modoLayout}
            gruposExtras={oficina.ferramentasTrilha}
            gruposExtrasNoTopo={oficina.ferramentasTrilha != null}
          />
        )
      }
      dockCamera={
        <DockCamera3D
          camera={{
            executar: (tipo) => camera.executar(tipo, oficina.idSelecionado ?? null),
            vistaAtiva: camera.vistaAtiva,
          }}
          temSelecao={oficina.idSelecionado != null}
          modoLayout={modoLayout}
          comEnquadrarCena={oficina.comEnquadrarCena}
        />
      }
      inspetor={
        modoLayout === 'mobile' ? undefined : (
          <InspetorCAD
            tituloModulo={modulo.titulo}
            modoLayout={modoLayout}
            recolhido={inspetorRecolhido}
            aoAlternarRecolhido={() => setInspetorRecolhido((v) => !v)}
            conteudoPropriedades={ferramentas.inspetor ?? null}
            conteudoOperacao={oficina.operacao}
            conteudoChecklist={oficina.checklist}
            somentePropriedades={oficina.operacao == null && oficina.checklist == null}
            aoLimparSelecao={oficina.aoClicarVazio}
          />
        )
      }
      barraEstado={<BarraEstado itens={itensEstado} />}
      rodape={
        <footer className="aviso" role="note">
          {AVISO_DIDATICO}
        </footer>
      }
    />
  )
}
